/**
 * Polyfill for `globalThis`.
 * This ensures compatibility with older JavaScript environments like the Android System WebView
 * on older OS versions where `globalThis` is not natively defined.
 * The `as any` cast is used to bypass TypeScript's read-only property check,
 * which is safe here because this code only runs when `globalThis` is undefined.
 */
if (typeof globalThis === 'undefined') {
	(function () {
		if (typeof self !== 'undefined') {
			(self as any).globalThis = self;
		} else if (typeof window !== 'undefined') {
			(window as any).globalThis = window;
		} else if (typeof global !== 'undefined') {
			(global as any).globalThis = global;
		}
		// No need for the complex Object.defineProperty fallback in most webview cases.
	}());
}

import { useRoutes } from "react-router-dom";
import router from "src/router";

import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";

import { Backdrop, CircularProgress, CssBaseline } from "@mui/material";
import ThemeProvider from "./theme/ThemeProvider";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";

function App() {
	const content = useRoutes(router);
	const source = new URLSearchParams(location.search).get("source");
	const referral = new URLSearchParams(location.search).get("referral");
	const [isLogoutLoading, setIsLogoutLoading] = useState(false);
	const [, setUserRenderTick] = useState(0);

	useEffect(() => {
		if (source) {
			Cookies.set("source", source);
		}
		if (referral) {
			if (!Cookies.get("referral")) {
				Cookies.set("referral", referral);
			}
		}
	}, []);

	useEffect(() => {
		// Listen for global logout loading events to show a full-screen overlay
		const handleLogoutLoading = (event: Event) => {
			const customEvent = event as CustomEvent<boolean>;
			setIsLogoutLoading(Boolean(customEvent.detail));
		};

		// Re-render app tree when user data is updated in localStorage.
		const handleUserUpdated = () => {
			setUserRenderTick((prev) => prev + 1);
		};

		window.addEventListener("logout-loading", handleLogoutLoading as EventListener);
		window.addEventListener("user-updated", handleUserUpdated as EventListener);
		return () => {
			window.removeEventListener("logout-loading", handleLogoutLoading as EventListener);
			window.removeEventListener("user-updated", handleUserUpdated as EventListener);
		};
	}, []);

	return (
		<ThemeProvider>
			<LocalizationProvider dateAdapter={AdapterDateFns}>
				<CssBaseline />
				{/* Full-screen loading during logout */}
				<Backdrop
					open={isLogoutLoading}
					sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 2 }}>
					<CircularProgress color="inherit" />
					<span style={{ marginLeft: 12, fontWeight: 600 }}>Logging out...</span>
				</Backdrop>
				{content}
			</LocalizationProvider>
		</ThemeProvider>
	);
}
export default App;
